import React from 'react';

import { LazyLoadImage } from '@depop/web-ui-kit/LazyLoadImage';
import { useResponsive } from '@depop/web-ui-kit/useResponsive';

import { ShopProductCard } from '@/app/[locale]/[username]/(shop)/components/ProductCard';
import { ShopProfileSection } from '@/modules/activityTracker/constants';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import {
  buildProductImageProps,
  picturePreviewAdapter,
} from '@/modules/pictures/helpers';
import { getProductPreview } from '@/modules/pictures/helpers/getProductPreview';
import { useActiveShopTab } from '@/modules/shop/hooks/useActiveShopTab';
import { useIsViewingOwnShop } from '@/modules/shop/hooks/useIsViewingOwnShop';
import { useShopByUsername } from '@/modules/shop/hooks/useShopByUsername';
import { type ShopListProduct } from '@/modules/shop/types';

type Props = {
  product: ShopListProduct;
  isRearrangeModeActive?: boolean;
  activeTab?: ShopProfileSection;
};

export function ProductListCard({
  product,
  isRearrangeModeActive,
  activeTab: activeTabProp,
}: Props) {
  const { tab: tabFromUrl } = useActiveShopTab();
  const tab = activeTabProp ?? tabFromUrl;
  const isViewingOwnShop = useIsViewingOwnShop();
  const { isMinMedium } = useResponsive();
  const { data } = useShopByUsername();
  const seller = data?.data;
  const { web_9276_like_icon_on_shop_pages } = useExperiments([
    'web_9276_like_icon_on_shop_pages',
  ]);
  const isOwnLikesTab = isViewingOwnShop && tab === ShopProfileSection.Likes;
  const showLike =
    web_9276_like_icon_on_shop_pages === VARIANT_IDENTIFIER &&
    (!isViewingOwnShop || isOwnLikesTab || tab === ShopProfileSection.Saved);

  const sellerUserName = [
    ShopProfileSection.Shop,
    ShopProfileSection.Selling,
    ShopProfileSection.Sold,
  ].includes(tab)
    ? seller?.username
    : undefined;

  function getAdditionalShopProductCardProps() {
    const hasMoreThanOnePicture =
      product.pictures && product.pictures.length > 1;
    const showSecondImageOnHover = hasMoreThanOnePicture && isMinMedium;

    if (showSecondImageOnHover) {
      return {
        HoverOverlay: () => (
          <LazyLoadImage
            {...buildProductImageProps({
              preview: picturePreviewAdapter(product.pictures?.[1]),
              sellerUserName,
            })}
          />
        ),
      };
    }

    return undefined;
  }

  return (
    <ShopProductCard
      status={product.status}
      preview={getProductPreview(product)}
      price={{
        currencyName: product.pricing.currency as CurrencyCode,
        priceAmount: product.pricing.original_price.total_price,
        discountedPriceAmount: product.pricing.discounted_price?.total_price,
      }}
      displayPrice={product.pricing.display_price}
      slug={product.slug}
      showBoostedTag={
        product.is_boosted &&
        tab === ShopProfileSection.Shop &&
        isViewingOwnShop
      }
      sellerUserName={sellerUserName}
      isRearrangeModeActive={isRearrangeModeActive}
      likeProps={
        showLike
          ? {
              productId: product.id,
              isLiked: isOwnLikesTab || product.is_liked,
            }
          : undefined
      }
      {...(!isRearrangeModeActive && getAdditionalShopProductCardProps())}
    />
  );
}
